import React, { useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScoreChip } from './ScoreChip';
import { formatPrice } from '../lib/currency';
import { Plane, Clock, Luggage, Heart, ExternalLink, PlaneTakeoff, PlaneLanding, ChevronDown, Share2 } from 'lucide-react';
import { shareFlight } from '../lib/share';

function fmtTime(iso) {
  try { return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }); }
  catch { return '--:--'; }
}
function fmtDate(str) {
  try { return new Date(str).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }); }
  catch { return str || ''; }
}
function fmtDur(min) {
  const m = Math.max(0, Math.round(min || 0));
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

// Full per-segment breakdown for one journey: each flight leg + layover duration/airport.
const JourneyDetail = ({ journey, title }) => {
  if (!journey || !(journey.segments || []).length) return null;
  const segs = journey.segments;
  const layovers = journey.layovers || [];
  const stops = journey.stops ?? 0;
  // Fallback (indicative) fares expose the stop count but not the per-segment breakdown.
  const missingBreakdown = stops > 0 && segs.length < stops + 1;
  return (
    <div className="rounded-xl border border-border bg-secondary/20 p-3" data-testid="journey-detail">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">{title}</p>
      <div className="space-y-2">
        {segs.map((s, i) => (
          <React.Fragment key={i}>
            <div className="flex items-start gap-3 text-xs">
              <div className="w-14 shrink-0 font-mono text-muted-foreground">{fmtTime(s.depart_time)}</div>
              <div className="flex-1 min-w-0">
                <p className="font-medium">{s.from} <span className="text-muted-foreground">→</span> {s.to}</p>
                <p className="text-muted-foreground">{s.airline_code} {s.flight_number} · {fmtDur(s.duration_minutes)}</p>
              </div>
              <div className="w-14 shrink-0 text-right font-mono text-muted-foreground">{fmtTime(s.arrive_time)}</div>
            </div>
            {i < segs.length - 1 && (
              <div className="ml-14 pl-3 border-l-2 border-dashed border-amber-400/60 py-1" data-testid="layover-row">
                <p className="text-[11px] text-amber-700 font-medium">
                  Layover at {layovers[i]?.airport || segs[i].to}{layovers[i]?.minutes ? ` · ${fmtDur(layovers[i].minutes)}` : ''}
                </p>
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
      {missingBreakdown && (
        <p className="mt-2 text-[11px] text-amber-700 font-medium" data-testid="journey-detail-stops-note">
          {stops} stop{stops > 1 ? 's' : ''} · full connection & layover details shown on the booking page
        </p>
      )}
    </div>
  );
};

const BADGE_STYLES = {
  CHEAPEST: 'bg-emerald-500/12 text-emerald-700 border-emerald-500/30',
  'BEST VALUE': 'bg-accent/12 text-accent border-accent/30',
  FASTEST: 'bg-sky-500/12 text-sky-700 border-sky-500/30',
};

// Renders a single directional journey (outbound OR return).
const JourneyRow = ({ journey, label, Icon, dateStr, testid }) => {
  if (!journey) return null;
  const stops = journey.stops ?? 0;
  const stopsLabel = stops === 0 ? 'Non-stop' : `${stops} stop${stops > 1 ? 's' : ''}`;
  const layovers = journey.layovers || [];
  return (
    <div className="flex items-center gap-3 min-w-0" data-testid={testid}>
      <div className="w-[92px] shrink-0">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          <Icon className="h-3.5 w-3.5 text-accent" /> {label}
        </span>
        {dateStr && <p className="text-[11px] text-muted-foreground/80 mt-0.5">{fmtDate(dateStr)}</p>}
      </div>
      <div className="flex-1 min-w-0 flex items-center gap-3">
        <div className="text-center shrink-0">
          <p className="text-base font-semibold tabular leading-tight">{fmtTime(journey.depart_time)}</p>
          <p className="text-xs font-mono text-muted-foreground">{journey.origin}</p>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1 text-[11px] text-muted-foreground justify-center">
            <Clock className="h-3 w-3" /> {fmtDur(journey.duration_minutes)}
          </div>
          <div className="relative my-1">
            <div className="h-px bg-border" />
            <Plane className="h-3.5 w-3.5 text-accent absolute left-1/2 -translate-x-1/2 -top-1.5 bg-card" />
          </div>
          <p className="text-[11px] text-center text-muted-foreground truncate">
            {stopsLabel}{layovers.length ? ` · via ${layovers.map((l) => l.airport).join(', ')}` : ''}
          </p>
        </div>
        <div className="text-center shrink-0">
          <p className="text-base font-semibold tabular leading-tight">{fmtTime(journey.arrive_time)}</p>
          <p className="text-xs font-mono text-muted-foreground">{journey.destination}</p>
        </div>
      </div>
    </div>
  );
};

export const FlightResultCard = ({ offer, currency, onViewDeal, onSave, saved }) => {
  const [showAgencies, setShowAgencies] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const isMultiCity = offer.trip_type === 'multi_city' && Array.isArray(offer.segments_all) && offer.segments_all.length >= 3;
  const isRoundTrip = offer.trip_type === 'round_trip' && !!offer.return;
  // Prefer structured journeys; fall back to top-level fields for legacy/cached offers.
  const outbound = offer.outbound || {
    origin: offer.origin, destination: offer.destination,
    depart_time: offer.depart_time, arrive_time: offer.arrive_time,
    duration_minutes: offer.duration_minutes, stops: offer.stops, layovers: offer.layovers,
  };
  const ret = offer.return || null;
  const agencies = Array.isArray(offer.agencies) ? offer.agencies : [];
  const hasMoreAgencies = offer.buy_kind === 'tickets' && agencies.length > 1;
  // Shrink the price for longer amounts (5+ digits) so it never overflows/overlaps.
  const priceStr = formatPrice(offer.price_inr, currency);
  const priceDigits = (priceStr.match(/\d/g) || []).length;
  const priceSize = priceDigits >= 7 ? 'text-lg' : priceDigits >= 5 ? 'text-xl' : 'text-2xl';

  return (
    <div className="rounded-2xl border border-border bg-card card-elevate overflow-hidden" data-testid="flight-result-card">
      {/* Header: badges (left) + save/share actions pinned to the top-right corner */}
      <div className="flex items-start justify-between gap-2 px-4 pt-4 pb-1">
        <div className="flex flex-wrap gap-1.5 min-w-0">
          {offer.badges?.map((b) => (
            <Badge key={b} variant="outline" className={`rounded-full text-[10px] font-semibold tracking-wide ${BADGE_STYLES[b] || ''}`} data-testid={`badge-${b.toLowerCase().replace(' ', '-')}`}>{b}</Badge>
          ))}
        </div>
        <div className="flex items-center gap-1.5 shrink-0" data-testid="flight-card-actions">
          <Button variant="outline" size="icon" className="h-9 w-9 rounded-xl shrink-0" onClick={() => onSave?.(offer)} data-testid="save-item-button">
            <Heart className={`h-4 w-4 ${saved ? 'fill-accent text-accent' : ''}`} />
          </Button>
          <Button variant="outline" size="icon" className="h-9 w-9 rounded-xl shrink-0" onClick={() => shareFlight(offer, currency)} title="Share this deal" data-testid="share-deal-button">
            <Share2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <div className="px-4 pb-4 pt-1 flex flex-col lg:flex-row lg:items-center gap-4">
        {/* Airline + trip type */}
        <div className="lg:w-52 shrink-0 min-w-0 flex items-start gap-3" data-testid="flight-card-airline">
          <span className="grid place-items-center h-10 w-10 rounded-xl bg-primary text-primary-foreground font-mono font-semibold text-sm shrink-0">{offer.airline_logo}</span>
          <div className="min-w-0">
            <p className="font-semibold text-sm truncate">{offer.airline_name}</p>
            <p className="text-xs text-muted-foreground">{offer.flight_number} · {(offer.cabin || 'economy').replace('_', ' ')}</p>
            <Badge variant="outline" className="mt-1.5 rounded-full text-[10px] font-semibold border-border text-muted-foreground" data-testid="flight-card-trip-type">
              {isMultiCity ? 'Multi-city' : isRoundTrip ? 'Round trip' : 'One way'}
            </Badge>
          </div>
        </div>

        {/* Journeys */}
        <div className="flex-1 min-w-0 space-y-2.5" data-testid="flight-card-times">
          {isMultiCity ? (
            offer.segments_all.map((j, i) => (
              <React.Fragment key={i}>
                {i > 0 && <div className="border-t border-dashed border-border" />}
                <JourneyRow journey={j} label={`Flight ${i + 1}`} Icon={PlaneTakeoff} dateStr={j.date} testid={`flight-card-segment-${i}`} />
              </React.Fragment>
            ))
          ) : (
            <>
              <JourneyRow journey={outbound} label="Depart" Icon={PlaneTakeoff} dateStr={offer.depart_date} testid="flight-card-outbound" />
              {isRoundTrip && <div className="border-t border-dashed border-border" />}
              {isRoundTrip && (
                <JourneyRow journey={ret} label="Return" Icon={PlaneLanding} dateStr={offer.return_date} testid="flight-card-return" />
              )}
            </>
          )}
        </div>

        {/* Price */}
        <div className="lg:w-44 shrink-0 border-t lg:border-t-0 border-border pt-3 lg:pt-0 lg:text-right">
          <ScoreChip score={offer.score} breakdown={offer.score_breakdown} size="sm" />
          <p className={`${priceSize} font-semibold tabular mt-1 whitespace-nowrap`} data-testid="flight-card-price">{priceStr}</p>
          <p className="text-[11px] text-muted-foreground" data-testid="flight-card-provider">
            {isRoundTrip ? 'round-trip total' : isMultiCity ? 'trip total' : 'total'}{offer.is_mock ? ' · demo' : ''}
          </p>
        </div>
      </div>

      {/* Flight meta (baggage · total · via) — full-width strip so it never overlaps the CTAs */}
      <div className="px-4 pb-3 -mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground" data-testid="flight-card-meta">
        <span className="inline-flex items-center gap-1"><Luggage className="h-3 w-3" /> {offer.baggage_checked_kg > 0 ? `${offer.baggage_checked_kg}kg checked` : 'Cabin baggage'}</span>
        {(isRoundTrip || isMultiCity) && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> total {fmtDur(offer.total_duration_minutes)}</span>}
        {offer.agencies_count > 0 && (
          <span className="inline-flex items-center gap-1">via <span className="font-medium text-foreground/80">{offer.agencies_count} {offer.agencies_count === 1 ? 'agency' : 'agencies'}</span></span>
        )}
      </div>

      {/* Actions footer — centered at the card bottom so CTAs never overlap the flight details */}
      <div className="px-4 pb-4 pt-3 border-t border-border flex flex-col sm:flex-row items-center justify-center gap-2" data-testid="flight-card-actions-footer">
        <Button onClick={() => onViewDeal(offer)} className="w-full sm:w-auto sm:min-w-[220px] h-11 rounded-xl bg-accent text-accent-foreground hover:bg-accent/90 font-semibold" data-testid="view-deal-button">
          {offer.buy_kind === 'tickets' ? 'BOOK NOW' : 'VIEW DEAL'} <ExternalLink className="h-4 w-4 ml-1.5" />
        </Button>
        {hasMoreAgencies && (
          <button onClick={() => setShowAgencies((v) => !v)} className="w-full sm:w-auto h-11 px-4 rounded-xl bg-accent/10 border border-accent/40 text-accent text-sm font-bold inline-flex items-center justify-center gap-1.5 hover:bg-accent/20 transition-colors" data-testid="toggle-agencies-button">
            Compare {agencies.length} agency prices <ChevronDown className={`h-4 w-4 transition-transform ${showAgencies ? 'rotate-180' : ''}`} />
          </button>
        )}
        <button onClick={() => setShowDetails((v) => !v)} className="w-full sm:w-auto h-11 px-4 rounded-xl border border-accent/40 text-accent hover:bg-accent/10 text-sm font-semibold inline-flex items-center justify-center gap-1.5 transition-colors" data-testid="toggle-details-button">
          View details <ChevronDown className={`h-4 w-4 transition-transform ${showDetails ? 'rotate-180' : ''}`} />
        </button>
        {!hasMoreAgencies && <p className="text-[10px] text-muted-foreground text-center sm:ml-1" data-testid="view-deal-opens-partner-subtext">Opens partner website</p>}
      </div>

      {/* Full flight details: segments, layovers, baggage — review before booking */}
      {showDetails && (
        <div className="border-t border-border bg-card px-4 py-3 space-y-2" data-testid="flight-details-section">
          {isMultiCity ? (
            offer.segments_all.map((j, i) => <JourneyDetail key={i} journey={j} title={`Flight ${i + 1} · ${j.origin} → ${j.destination}`} />)
          ) : (
            <>
              <JourneyDetail journey={outbound} title={`Outbound · ${outbound.origin} → ${outbound.destination}`} />
              {isRoundTrip && <JourneyDetail journey={ret} title={`Return · ${ret.origin} → ${ret.destination}`} />}
            </>
          )}
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground pt-1" data-testid="flight-details-baggage">
            <span className="inline-flex items-center gap-1"><Luggage className="h-3 w-3" /> Cabin: {offer.baggage_cabin_kg ? `${offer.baggage_cabin_kg}kg` : 'as per airline'}</span>
            <span className="inline-flex items-center gap-1"><Luggage className="h-3 w-3" /> Checked: {offer.baggage_checked_kg > 0 ? `${offer.baggage_checked_kg}kg` : 'not included'}</span>
            <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> Total {fmtDur(offer.total_duration_minutes || outbound.duration_minutes)}</span>
            <span>Cabin class: {(offer.cabin || 'economy').replace('_', ' ')}</span>
          </div>
          <Button onClick={() => onViewDeal(offer)} className="w-full h-10 rounded-xl bg-accent text-accent-foreground hover:bg-accent/90 font-semibold mt-1" data-testid="details-book-button">
            {offer.buy_kind === 'tickets' ? 'BOOK THIS FLIGHT' : 'VIEW DEAL'} <ExternalLink className="h-4 w-4 ml-1.5" />
          </Button>
        </div>
      )}

      {/* Per-agency prices (real-time proposals) */}
      {hasMoreAgencies && showAgencies && (
        <div className="border-t border-border bg-secondary/30 px-4 py-3 space-y-1.5" data-testid="agencies-list">
          {agencies.map((a) => (
            <div key={a.proposal_id} className="flex items-center justify-between gap-2" data-testid={`agency-row-${a.agent_id}`}>
              <span className="text-sm font-medium truncate">{a.agent}</span>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-sm font-semibold tabular">{formatPrice(a.price_inr, currency)}</span>
                <Button size="sm" variant="outline" className="h-8 rounded-lg" onClick={() => onViewDeal(offer, a.proposal_id)} data-testid={`agency-book-${a.agent_id}`}>
                  Book <ExternalLink className="h-3.5 w-3.5 ml-1" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
